import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import courseService, { Course, IndustryCategory, CourseStats, CourseFilters } from '../services/courseService';
import { useAuth } from '../contexts/AuthContext';
import RedactedValue from '../components/RedactedValue';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ClockIcon,
  StarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CoursesPage: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [industryCategories, setIndustryCategories] = useState<IndustryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<CourseFilters>({});
  const [userEnrollments, setUserEnrollments] = useState<Set<string>>(new Set());


  const difficultyOptions = [
    { value: 'beginner', label: 'Beginner', color: 'bg-green-100 text-green-800' },
    { value: 'intermediate', label: 'Intermediate', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'advanced', label: 'Advanced', color: 'bg-red-100 text-red-800' }
  ];

  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'demand', label: 'Most In-Demand' },
    { value: 'salary', label: 'Highest Salary' },
    { value: 'duration', label: 'Shortest Duration' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchUserEnrollments();
    }
  }, [currentUser]);

  // Extract search query from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
    }
  }, [location.search]);

  useEffect(() => {
    applyFilters();
  }, [courses, activeFilters, searchQuery]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [coursesData, categoriesData] = await Promise.all([
        courseService.getCourses(),
        courseService.getIndustryCategories()
      ]);
      
      setCourses(coursesData.courses);
      setIndustryCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };



  const fetchUserEnrollments = async () => {
    try {
      if (!currentUser) {
        setUserEnrollments(new Set());
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/courses/my/enrolled`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const enrolledCourseIds = new Set<string>(result.data.map((enrollment: any) => enrollment.courseId));
          setUserEnrollments(enrolledCourseIds);
        }
      }
    } catch (error) {
      console.error('Error fetching user enrollments:', error);
    }
  };







  const applyFilters = () => {
    let filtered = [...courses];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(query) ||
        course.description.toLowerCase().includes(query) ||
        (course.skills || []).some(skill => skill.toLowerCase().includes(query)) ||
        (course.tags || []).some(tag => tag.toLowerCase().includes(query))
      );
    }
    if (activeFilters.industryCategory) {
      filtered = filtered.filter(course => course.industryCategory === activeFilters.industryCategory);
    }

    if (activeFilters.difficulty) {
      filtered = filtered.filter(course => course.difficulty === activeFilters.difficulty);
    }

    if (activeFilters.minDuration) {
      filtered = filtered.filter(course => course.duration >= activeFilters.minDuration!);
    }

    if (activeFilters.maxDuration) {
      filtered = filtered.filter(course => course.duration <= activeFilters.maxDuration!);
    }

    if (activeFilters.minSalary) {
      filtered = filtered.filter(course => course.marketData.averageSalary >= activeFilters.minSalary!);
    }
    if (activeFilters.sortBy) {
      switch (activeFilters.sortBy) {
        case 'popular':
          filtered.sort((a, b) => b.enrollmentCount - a.enrollmentCount);
          break;
        case 'rating':
          filtered.sort((a, b) => b.averageRating - a.averageRating);
          break;
        case 'demand':
          filtered.sort((a, b) => b.marketData.jobDemand - a.marketData.jobDemand);
          break;
        case 'salary':
          filtered.sort((a, b) => b.marketData.averageSalary - a.marketData.averageSalary);
          break;
        case 'duration':
          filtered.sort((a, b) => a.duration - b.duration);
          break;
      }
    }

    setFilteredCourses(filtered);
  };

  const handleFilterChange = (key: keyof CourseFilters, value: any) => {
    setActiveFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery('');
  };


  const getFilterCount = () => {
    return Object.values(activeFilters).filter(value => value !== undefined && value !== '').length + (searchQuery ? 1 : 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Course Catalog
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Master in-demand skills with our comprehensive courses. Learn from industry experts, 
              earn certifications, and boost your career prospects.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses, skills, or technologies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 relative"
            >
              <FunnelIcon className="h-5 w-5" />
              Filters
              {getFilterCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {getFilterCount()}
                </span>
              )}
            </button>
            <select
              value={activeFilters.sortBy || ''}
              onChange={(e) => handleFilterChange('sortBy', e.target.value || undefined)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Sort by...</option>
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {getFilterCount() > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-sm text-gray-500">Active filters:</span>
              {searchQuery && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">
                  Search: "{searchQuery}"
                  <button onClick={() => setSearchQuery('')}>
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              )}
              {Object.entries(activeFilters).map(([key, value]) => {
                if (!value) return null;
                return (
                  <span key={key} className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">
                    {key}: {value.toString()}
                    <button onClick={() => handleFilterChange(key as keyof CourseFilters, undefined)}>
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </span>
                );
              })}
              <button 
                onClick={clearFilters}
                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {showFilters && (
            <div className="w-80 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                  <button 
                    onClick={() => setShowFilters(false)}
                    className="lg:hidden"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Industry
                    </label>
                    <select
                      value={activeFilters.industryCategory || ''}
                      onChange={(e) => handleFilterChange('industryCategory', e.target.value || undefined)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">All Industries</option>
                      {industryCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.icon} {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Difficulty
                    </label>
                    <div className="space-y-2">
                      {difficultyOptions.map(option => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="radio"
                            name="difficulty"
                            value={option.value}
                            checked={activeFilters.difficulty === option.value}
                            onChange={(e) => handleFilterChange('difficulty', e.target.checked ? e.target.value : undefined)}
                            className="mr-2"
                          />
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${option.color}`}>
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (hours)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={activeFilters.minDuration || ''}
                        onChange={(e) => handleFilterChange('minDuration', e.target.value ? parseInt(e.target.value) : undefined)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={activeFilters.maxDuration || ''}
                        onChange={(e) => handleFilterChange('maxDuration', e.target.value ? parseInt(e.target.value) : undefined)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Average Salary (minimum)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 70000"
                      value={activeFilters.minSalary || ''}
                      onChange={(e) => handleFilterChange('minSalary', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {getFilterCount() > 0 && (
                  <button 
                    onClick={clearFilters}
                    className="w-full mt-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">
                Showing {filteredCourses.length} of {courses.length} courses
              </p>
            </div>

            {filteredCourses.length === 0 ? (
              <div className="text-center py-12">
                <AcademicCapIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-4">
                  {courses.length === 0 ? 'No courses available yet.' : 'No courses found matching your criteria.'}
                </p>
                
                {courses.length > 0 && (
                  <button 
                    onClick={clearFilters}
                    className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Clear filters to see all courses
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses.map(course => (
                  <CourseCard 
                    key={course.id} 
                    course={course} 
                    isEnrolled={currentUser ? userEnrollments.has(course.id) : false}
                    isAuthenticated={!!currentUser}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
const CourseCard: React.FC<{ course: Course; isEnrolled: boolean; isAuthenticated: boolean }> = ({ course, isEnrolled, isAuthenticated }) => {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const formatSalary = (salary: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(salary);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'rising':
        return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
      case 'declining':
        return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
      default:
        return <MinusIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleCourseClick = () => {
    // For now, allow all users to view course details
    window.location.href = `/courses/${course.id}`;
  };

  const handleEnrollClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
    } else {
      // Navigate to course page for enrolled users
      window.location.href = `/courses/${course.id}`;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer">
      <div onClick={handleCourseClick}>
      <div className="aspect-video bg-gray-200 relative">
        {course.thumbnailUrl ? (
          <img 
            src={course.thumbnailUrl} 
            alt={course.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <span className="text-4xl">📚</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
            {course.difficulty}
          </span>
          {course.price === 0 && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500 text-white">
              FREE
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1">
          {getTrendIcon(course.marketData?.marketTrend || 'stable')}
        </div>
      </div>
      <div className="p-4">
        <div className="mb-2">
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
            {course.title}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            by {course.instructorName}
          </p>
        </div>

        <p className="text-gray-600 text-sm line-clamp-2 mb-3">
          {course.shortDescription}
        </p>
        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          <div className="flex items-center gap-1">
            <ClockIcon className="h-4 w-4" />
            {course.duration || 0}h
          </div>
          <div className="flex items-center gap-1">
            <UsersIcon className="h-4 w-4" />
            {/* The API omits enrollmentCount entirely when signed out. */}
            {course.enrollmentCountLocked ? (
              <RedactedValue bars={3} cta="Sign in" />
            ) : (
              (course.enrollmentCount || 0).toLocaleString()
            )}
          </div>
          <div className="flex items-center gap-1">
            <StarIcon className="h-4 w-4 fill-current text-yellow-400" />
            {(course.averageRating || 0).toFixed(1)}
          </div>
        </div>
        <div className="mb-3">
          <div className="flex flex-wrap gap-1">
            {(course.skills || []).slice(0, 3).map(skill => (
              <span key={skill} className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-md">
                {skill}
              </span>
            ))}
            {(course.skills || []).length > 3 && (
              <span className="px-2 py-1 bg-gray-50 text-gray-500 text-xs rounded-md">
                +{(course.skills || []).length - 3} more
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between text-sm mb-4">
          <div className="flex items-center gap-1 text-green-600">
            <CurrencyDollarIcon className="h-4 w-4" />
            {formatSalary(course.marketData?.averageSalary || 0)}
          </div>
          <div className="flex items-center gap-1 text-purple-600">
            <TrophyIcon className="h-4 w-4" />
            {course.marketData?.jobDemand || 0} jobs
          </div>
        </div>
        </div>
        <button 
          onClick={handleEnrollClick}
          className={`w-full py-2 px-4 rounded-md transition-colors font-medium text-center ${
            isEnrolled 
              ? 'bg-green-600 text-white hover:bg-green-700' 
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          {isEnrolled 
            ? 'Continue Learning' 
            : ((course.price || 0) === 0 ? 'Enroll Free' : `Enroll - $${course.price || 0}`)
          }
        </button>

        {/* Login Prompt Modal */}
        {showLoginPrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
                  <AcademicCapIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Enroll in Course</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Create an account or sign in to enroll in courses and track your learning progress.
                </p>
                <div className="flex gap-3">
                  <Link
                    to="/register"
                    className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 text-sm font-medium text-center"
                  >
                    Sign Up Free
                  </Link>
                  <Link
                    to="/login"
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 text-sm font-medium text-center"
                  >
                    Log In
                  </Link>
                </div>
                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="mt-3 text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage;